import NextSection from "@components/About/NextSection";
import Button from "@components/Button";
import Checkbox from "@components/Formik/Checkbox";
import DatePicker2 from "@components/Formik/DatePicker2";
import Input from "@components/Formik/Input";
import Select2 from "@components/Formik/Select2";
import TextArea from "@components/Formik/TextArea";
import ShapeArrow from "@components/Icons/ShapeArrow";
import Link from "@components/Link";
import Modal from "@components/Modal";
import gstyles from "@gstyles/index";
import FormProvider from "@providers/FormProvider";
import ModalProvider, { useModalContext } from "@providers/ModalProvider";
import { useTranslationContext } from "@providers/TranslationProvider";
import MeetingAndEventsService from "@services/MeetingAndEventsService";
import cx from "classnames";
import { Form, useFormik } from "formik";
import _ from "lodash";
import styles from "./RequestForProposal.module.scss";
import Yup from "@utils/yup";
import { EMAIL } from "@utils/validate";
import { useMemo } from "react";
import { useRouteContext } from "@providers/RouteProvider";
import { usePageContext } from "@providers/PageProvider";
import { meetingPage } from "@utils/helper";

const ModalSuccess = () => {
  const { modalService } = useModalContext();
  const { i18n } = useTranslationContext();
  const handler = {
    onClose: () => {
      modalService.hide();
    },
  };
  return (
    <Modal
      style={{
        content: { background: "white !important" },
      }}
      isOpen={modalService.visible}
      onRequestClose={handler.onClose}
    >
      <div className={styles.modal}>
        <div className="flex gap-2">
          {gstyles.icons({
            name: "check-outline",
            size: 24,
            color: gstyles.colors.green[600],
          })}
          {i18n.t("Notification.submitSuccess")}
        </div>
      </div>
    </Modal>
  );
};

const FormView = () => {
  const { router } = useRouteContext();
  const { i18n } = useTranslationContext();
  const { modalService } = useModalContext();
  const { pageData } = usePageContext();
  const proposal = meetingPage.formatRequestForProposal(pageData);
  const form = useFormik({
    initialValues: {
      fullname: "",
      email: "",
      phone: "",
      numberOfAttendees: "",
      desiredSetup: "",
      date: "",
      overview: "",
      privacyPolicyAgreement: false,
      acceptsMarketing: false,
    },
    onSubmit: async (value) => {
      const payload = { data: value };
      const res = await MeetingAndEventsService.send(payload);
      if (_.has(res, "data")) {
        modalService.addDOM(<ModalSuccess />);
      }
    },
    validationSchema: Yup.object().shape({
      fullname: Yup.string().required(i18n.t("Form.required")),
      email: Yup.string()
        .required(i18n.t("Form.required"))
        .matches(EMAIL, i18n.t("Form.emailInvalid")),
      phone: Yup.string().required(i18n.t("Form.required")),
      privacyPolicyAgreement: Yup.boolean()
        .required(i18n.t("Form.mustBeAccept"))
        .oneOf([true], i18n.t("Form.mustBeAccept")),
      // acceptsMarketing: Yup.boolean()
      //   .required(i18n.t("Form.mustBeAccept"))
      //   .oneOf([true], i18n.t("Form.mustBeAccept")),
    }),
  });
  const enabledSubmit = useMemo(
    () => !form.isSubmitting && form.isValid && form.dirty,
    [form]
  );
  return (
    <section
      id="requestForProposal"
      data-scroll-section
      className={cx(styles.section)}
    >
      <NextSection type="dark" className={cx(styles.nextSection)}>
        {_.get(proposal, "title")}
      </NextSection>
      <div className={cx(styles.subtitleSection)}>
        {_.get(proposal, "content")}
      </div>
      <div className={cx(styles.formWrapper)}>
        <FormProvider form={form}>
          <Form>
            <div className={styles.form}>
              <div className={styles.half}>
                <Input
                  theme="dark"
                  name="fullname"
                  placeholder={i18n.t(
                    "MeetingAndEvents.requestForProposal.form.fullName"
                  )}
                  required
                />
                <Input
                  theme="dark"
                  name="email"
                  placeholder={i18n.t(
                    "MeetingAndEvents.requestForProposal.form.emailAddress"
                  )}
                  required
                />
                <Input
                  theme="dark"
                  name="phone"
                  placeholder={i18n.t(
                    "MeetingAndEvents.requestForProposal.form.phoneNumber"
                  )}
                  required
                />
                <Select2
                  optionCapitalize
                  theme="dark"
                  name="desiredSetup"
                  placeholder={i18n.t(
                    "MeetingAndEvents.requestForProposal.form.desiredSetup"
                  )}
                  options={JSON.parse(
                    i18n.t(
                      "MeetingAndEvents.requestForProposal.form.desiredSetupOptions"
                    )
                  )}
                />
                <Input
                  type="number"
                  theme="dark"
                  name="numberOfAttendees"
                  placeholder={i18n.t(
                    "MeetingAndEvents.requestForProposal.form.numberOfAttendees"
                  )}
                />
                <DatePicker2
                  dark
                  name="date"
                  placeholder={i18n.t(
                    "MeetingAndEvents.requestForProposal.form.eventDate"
                  )}
                />
              </div>

              <TextArea
                theme="dark"
                name="overview"
                placeholder={i18n.t(
                  "MeetingAndEvents.requestForProposal.form.note"
                )}
                required
              />
              <div className="flex flex-col">
                <div className="flex items-center">
                  <Checkbox
                    theme="dark"
                    name="privacyPolicyAgreement"
                    label={
                      <span className={cx(styles.checkboxLabel)}>
                        {i18n.t(
                          "MeetingAndEvents.requestForProposal.form.condition.readAndAccept"
                        )}{" "}
                        <Link
                          target={"_blank"}
                          className="text-yellow-1"
                          href={router.toUrl("privacy", {
                            legalNoticeType: "PrivacyPolicy",
                          })}
                        >
                          {i18n.t(
                            "MeetingAndEvents.requestForProposal.form.condition.privacyPolicy"
                          )}
                        </Link>
                      </span>
                    }
                  />
                </div>
                <div className="flex items-center mt-4">
                  <Checkbox
                    theme="dark"
                    name="acceptsMarketing"
                    label={
                      <span className={cx(styles.checkboxLabel)}>
                        {i18n.t(
                          "MeetingAndEvents.requestForProposal.form.condition.keepUpToDateInfo"
                        )}
                      </span>
                    }
                  />
                </div>
              </div>
              <Button
                disabled={!enabledSubmit}
                onClick={form.handleSubmit}
                className={cx(styles.button)}
                label={i18n.t("Button.submit")}
                type="primary"
                suffix={
                  <div className={cx("ml-auto", styles.btnSuffix)}>
                    <ShapeArrow />
                  </div>
                }
              />
            </div>
          </Form>
        </FormProvider>
      </div>
    </section>
  );
};
const View = (props: any) => {
  return (
    <ModalProvider>
      <FormView {...props} />
    </ModalProvider>
  );
};
export default View;
